// Helpers partagés entre la liste des sessions et le détail d'une session.
//
// Les métriques remontées par le mobile arrivent au fil de la partie : un champ
// absent signifie « pas encore reçu », ce qui n'est pas la même chose que 0.
// D'où la distinction entre `hasValue` (afficher un tiret) et `|| 0`.

// Les photos et les scores remontent pendant la partie : sans rafraîchissement
// périodique, le direct côté mobile ne se verrait jamais côté web.
export const POLL_INTERVAL_MS = 20000;

// Le mobile renseigne la fin de partie uniquement à la clôture : tant que
// `game_ended_at` est vide, la session est en cours.
export const isSessionLive = (session) =>
  !!session && !session.game_ended_at && !session.endedAt;

export const getSessionEndedAt = (session) =>
  session?.game_ended_at || session?.endedAt || null;

export const hasValue = (value) => value !== null && value !== undefined;

// Temps de jeu dérivé des horodatages plutôt que du champ `totalTime` remonté
// par le mobile : `totalTime` n'est renseigné qu'à la clôture de la partie, il
// reste donc vide pendant tout le direct. `created_at` est écrit au démarrage,
// on part de là — jusqu'à la fin de partie si elle est connue, jusqu'à
// maintenant sinon.
export const getElapsedMinutes = (session, nowMs = Date.now()) => {
  const startedAtMs = new Date(session?.created_at).getTime();
  if (Number.isNaN(startedAtMs)) return null;

  const endedAt = getSessionEndedAt(session);
  const endedAtMs = endedAt ? new Date(endedAt).getTime() : nowMs;
  if (Number.isNaN(endedAtMs)) return null;

  // Horloge du navigateur en retard sur celle du serveur : mieux vaut 0 qu'un
  // temps négatif à l'écran.
  return Math.max(0, Math.round((endedAtMs - startedAtMs) / 60000));
};

export const getTeamName = (session, t) => {
  if (session?.teamNameOverride) return session.teamNameOverride;
  if (session?.tourCode?.guestGameUser?.teamName)
    return session.tourCode.guestGameUser.teamName;
  return t("teamPhotos.anonymous");
};

export const getEmail = (session) =>
  session?.email || session?.tourCode?.guestGameUser?.email || null;

export const getPhotoCount = (session) => session?.teamPhotos?.length || 0;

// Normalise les photos d'une session pour l'affichage (URL signée, énigme).
export const getSessionPhotos = (session, t) =>
  (session?.teamPhotos || [])
    .map((teamPhoto) => ({
      id: teamPhoto.id,
      photoUrl: teamPhoto.teamPhoto?.signedUrl || teamPhoto.teamPhoto?.path,
      enigmaName: teamPhoto.enigma?.title || t("teamPhotos.unknown_enigma"),
      enigmaDescription: teamPhoto.enigma?.description || "",
      createdAt: teamPhoto.createdAt || session?.created_at,
    }))
    .filter((photo) => !!photo.photoUrl);

export const formatDateTime = (date) => {
  if (!date) return null;
  return new Date(date).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatTime = (date) => {
  if (!date) return null;
  return new Date(date).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

// `distanceTraveled` est envoyé en mètres par le mobile.
export const formatDistance = (meters) => {
  if (!hasValue(meters)) return null;
  if (meters < 1000) return { value: Math.round(meters), unit: "m" };
  return { value: (meters / 1000).toFixed(1).replace(".", ","), unit: "km" };
};

// Progression bornée à [0, 1] : le mobile peut remonter un réalisé supérieur au
// total (zone rejouée), la jauge ne doit pas déborder.
export const getRatio = (done, total) => {
  if (!hasValue(done) || !total) return 0;
  return Math.max(0, Math.min(1, done / total));
};
